import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import './PresenceOnboarding.css';
import axiosConn from '../axiosConn';
import { AuthContext } from '../context/AuthContext';
import { useSocket } from '../context/socket';
import { usePresence } from '../context/PresenceContext';
import { useConversations } from '../context/ConversationContext';

const HEX_ID_RE = /^[a-f0-9]{24}$/i;

const parseEnvList = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const DEFAULT_SALES_REPS = ['supportNET', 'jgdotnet'];
const SALES_REP_HANDLES = (() => {
  const fromEnv = parseEnvList(process.env.REACT_APP_PRESENCE_SALES_REPS);
  return fromEnv.length ? fromEnv : DEFAULT_SALES_REPS;
})();
const SALES_REP_IDS = parseEnvList(process.env.REACT_APP_PRESENCE_SALES_REP_IDS);
const DEFAULT_SUPPORT_HANDLE = SALES_REP_HANDLES[0] || null;
const DEFAULT_SUPPORT_ID =
  SALES_REP_IDS.find((val) => HEX_ID_RE.test(val)) || null;
const GHOST_TAB_NAME_PREFIX = 'presence-onboarding-ghost-';
const DEFAULT_AVATAR_URL =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';
const PRESENCE_DRAFT_MESSAGE =
  'Hi — I’m using JG Presence (Nebula) and would like to talk through next steps or questions when you’re available.';
const PRESENCE_DRAFT_KEY = 'jg_presence_draft_message';
const PRESENCE_DRAFT_TARGET_KEY = 'jg_presence_draft_target';
const PRESENCE_DRAFT_CONVO_KEY = 'jg_presence_draft_convo';
const normalizeIdentifier = (value) =>
  String(value || '')
    .trim()
    .replace(/^@/, '')
    .toLowerCase();

const normalizePresenceState = (status, onlineFlag) => {
  if (typeof onlineFlag === 'boolean' && onlineFlag === false) return 'offline';
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'online') return 'active';
  if (normalized === 'active' || normalized === 'available') return 'active';
  if (
    normalized === 'idle' ||
    normalized === 'away' ||
    normalized === 'background'
  )
    return 'idle';
  if (normalized === 'offline') return 'offline';
  if (onlineFlag === true) return 'active';
  return 'offline';
};

const isPresenceOnline = (entry) => {
  if (!entry) return false;
  const status =
    entry.status ||
    entry.presenceStatus ||
    (entry.online === true
      ? 'online'
      : entry.online === false
      ? 'offline'
      : null);
  const normalized = normalizePresenceState(status, entry.online);
  return normalized === 'active' || normalized === 'idle';
};

const pickPresenceId = (entry) => {
  if (!entry) return null;
  const candidates = [
    entry.playerId,
    entry._id,
    entry.mongoId,
    entry.accountId,
    entry.id,
  ]
    .filter(Boolean)
    .map((val) => String(val));
  const direct = candidates.find((val) => HEX_ID_RE.test(val));
  return direct || null;
};

export default function PresenceOnboarding() {
  const { player, updatePlayer } = useContext(AuthContext);
  const { socket } = useSocket() || {};
  const { presenceMap } = usePresence() || {};
  const {
    conversations = [],
    conversationsData = [],
  } = useConversations() || {};

  const location = useLocation();

  const playerHandle = player?.data?.player?.playerId || player?.playerId || 'player';
  const playerId = player?.data?.player?._id || player?._id || null;
  const presenceActive = Boolean(
    player?.data?.player?.presenceActive ??
      player?.presenceActive ??
      player?.data?.presenceActive ??
      false,
  );
  const presenceSdkDownloadIssuedAt =
    player?.data?.player?.presenceSdkDownloadIssuedAt ??
    player?.presenceSdkDownloadIssuedAt ??
    player?.data?.presenceSdkDownloadIssuedAt ??
    null;

  const [connectionStatus, setConnectionStatus] = useState(() =>
    socket?.connected ? 'Live' : 'Offline',
  );
  const [tabCount, setTabCount] = useState(1);
  const [ghostBlocked, setGhostBlocked] = useState(false);
  const [ghostHandleActive, setGhostHandleActive] = useState(false);
  const [ghostCloseFailed, setGhostCloseFailed] = useState(false);
  const [isGhostTab, setIsGhostTab] = useState(false);
  const [isIdleSimulating, setIsIdleSimulating] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [lastPresenceEvent, setLastPresenceEvent] = useState(null);
  const [lastSocketEvent, setLastSocketEvent] = useState(null);
  const [showPayload, setShowPayload] = useState(true);
  const [pricingVisible, setPricingVisible] = useState(false);
  const [heartbeatTick, setHeartbeatTick] = useState(0);
  const [heartbeatActive, setHeartbeatActive] = useState(false);
  const [reconnectTick, setReconnectTick] = useState(0);
  const [reconnectActive, setReconnectActive] = useState(false);
  const [callError, setCallError] = useState('');
  const [callLoading, setCallLoading] = useState(false);
  const [nebulaCheckoutError, setNebulaCheckoutError] = useState('');
  const [nebulaCheckoutLoading, setNebulaCheckoutLoading] = useState(false);
  const [sdkDownloadError, setSdkDownloadError] = useState('');
  const [sdkDownloadLoading, setSdkDownloadLoading] = useState(false);
  const [sdkFilename, setSdkFilename] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [fallbackPresenceMap, setFallbackPresenceMap] = useState(
    () => new Map(),
  );

  const ghostTabRef = useRef(null);
  const idleTimerRef = useRef(null);
  const disconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const hasConnectedRef = useRef(false);
  const wasDisconnectedRef = useRef(false);
  const refreshPlayerInFlightRef = useRef(false);
  const lastRefreshTokenRef = useRef(null);
  const requestedSalesRepSnapshotRef = useRef(false);
  const tabIdRef = useRef(
    `presence-onboarding-${Math.random().toString(36).slice(2)}`,
  );

  const presenceSourceMap = useMemo(() => {
    if (!presenceMap) return new Map();
    if (presenceMap instanceof Map) return presenceMap;
    return new Map(Object.entries(presenceMap));
  }, [presenceMap]);

  const getPresenceTimestamp = useCallback((entry) => {
    if (!entry) return 0;
    const ts =
      entry.lastSeenAt ??
      entry.lastSeen ??
      entry.lastUpdatedAt ??
      entry.updatedAt ??
      entry.timestamp ??
      entry.wentOfflineAt ??
      0;
    if (!ts) return 0;
    if (typeof ts === 'number') return ts;
    const parsed = new Date(ts).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  const presenceLookupMap = useMemo(() => {
    const keys = new Set([
      ...Array.from(presenceSourceMap.keys()),
      ...Array.from(fallbackPresenceMap.keys()),
    ]);
    if (!keys.size) return new Map();
    const next = new Map();
    keys.forEach((key) => {
      const primary = presenceSourceMap.get(key);
      const fallback = fallbackPresenceMap.get(key);
      if (!primary && fallback) {
        next.set(key, fallback);
        return;
      }
      if (primary && !fallback) {
        next.set(key, primary);
        return;
      }
      if (primary && fallback) {
        const primaryTs = getPresenceTimestamp(primary);
        const fallbackTs = getPresenceTimestamp(fallback);
        next.set(key, fallbackTs > primaryTs ? fallback : primary);
      }
    });
    return next;
  }, [fallbackPresenceMap, getPresenceTimestamp, presenceSourceMap]);

  const presenceEntries = useMemo(() => {
    if (!presenceLookupMap || !presenceLookupMap.size) return [];
    return Array.from(presenceLookupMap.entries()).map(([key, value]) => ({
      key,
      value,
    }));
  }, [presenceLookupMap]);

  const sdkIssuedLabel = useMemo(() => {
    if (!presenceSdkDownloadIssuedAt) return null;
    const issuedAt = new Date(presenceSdkDownloadIssuedAt);
    if (Number.isNaN(issuedAt.getTime())) return null;
    return issuedAt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }, [presenceSdkDownloadIssuedAt]);

  const sdkInstallFilename = useMemo(
    () => sdkFilename || 'jg-presence-sdk-<version>.tgz',
    [sdkFilename],
  );

  const sdkInstallCommand = useMemo(
    () => `npm install ./${sdkInstallFilename}`,
    [sdkInstallFilename],
  );

  const presenceProviderSnippet = useMemo(
    () => `<JGPresenceProvider>\n  <App />\n</JGPresenceProvider>`,
    [],
  );

  const presenceDebugSnippet = useMemo(
    () =>
      `import { useJGPresence } from '@jg/presence-sdk';\n\nfunction PresenceDebug() {\n  const presence = useJGPresence();\n\n  console.log('JG Presence state:', presence);\n\n  return null;\n}`,
    [],
  );

  const presenceDebugOutput = useMemo(
    () =>
      `JG Presence state: {\n  status: "online",\n  lastSeenAt: 17669039224,\n  deviceCount: 1,\n  networkQuality: "green"\n}`,
    [],
  );

  const salesRepIdentifiers = useMemo(() => {
    const identifiers = new Set();
    [...SALES_REP_HANDLES, ...SALES_REP_IDS].forEach((value) => {
      const normalized = normalizeIdentifier(value);
      if (normalized) identifiers.add(normalized);
    });
    return identifiers;
  }, []);

  const playerIdentifiers = useMemo(() => {
    return new Set(
      [playerId, playerHandle].filter(Boolean).map((val) => String(val)),
    );
  }, [playerId, playerHandle]);

  const findPresenceEntry = useCallback(
    (identifier) => {
      if (!identifier) return null;
      const key = String(identifier);
      const direct = presenceLookupMap.get(key);
      if (direct) return direct;
      const lowered = key.toLowerCase();
      const match = presenceEntries.find(({ key: entryKey, value }) => {
        const candidates = [
          entryKey,
          value?.playerId,
          value?._id,
          value?.mongoId,
          value?.handle,
          value?.displayName,
        ]
          .filter(Boolean)
          .map((val) => String(val));
        return candidates.some((val) => val.toLowerCase() === lowered);
      });
      return match?.value || null;
    },
    [presenceEntries, presenceLookupMap],
  );

  const currentPresence = useMemo(() => {
    return findPresenceEntry(playerId) || findPresenceEntry(playerHandle);
  }, [findPresenceEntry, playerHandle, playerId]);

  const presenceState = useMemo(() => {
    if (connectionStatus === 'Offline') return 'offline';
    const status =
      currentPresence?.status ||
      currentPresence?.presenceStatus ||
      (currentPresence?.online === true
        ? 'online'
        : currentPresence?.online === false
        ? 'offline'
        : null);
    return normalizePresenceState(status, currentPresence?.online);
  }, [connectionStatus, currentPresence]);

  const pulseState =
    connectionStatus === 'Offline' ? 'offline' : presenceState;

  const multiTabActive = tabCount > 1;

  const salesRepAvailable = useMemo(() => {
    for (const handle of SALES_REP_HANDLES) {
      const entry = findPresenceEntry(handle);
      if (entry && isPresenceOnline(entry)) {
        return { handle, entry };
      }
    }
    return null;
  }, [findPresenceEntry]);

  const salesRepTone = useMemo(() => {
    if (!salesRepAvailable?.entry) return 'offline';
    const normalized = normalizePresenceState(
      salesRepAvailable.entry.status || salesRepAvailable.entry.presenceStatus,
      salesRepAvailable.entry.online,
    );
    if (normalized === 'active') return 'online';
    if (normalized === 'idle') return 'idle';
    return 'offline';
  }, [salesRepAvailable]);

  const canTalkLive = Boolean(salesRepAvailable);

  const supportRepTarget = useMemo(() => {
    if (salesRepAvailable) {
      return {
        handle: salesRepAvailable.handle,
        entry: salesRepAvailable.entry,
        id: null,
      };
    }
    if (!DEFAULT_SUPPORT_HANDLE && !DEFAULT_SUPPORT_ID) return null;
    return {
      handle: DEFAULT_SUPPORT_HANDLE,
      entry: DEFAULT_SUPPORT_HANDLE
        ? findPresenceEntry(DEFAULT_SUPPORT_HANDLE)
        : null,
      id: DEFAULT_SUPPORT_ID,
    };
  }, [findPresenceEntry, salesRepAvailable]);

  const canInitiateContact = Boolean(supportRepTarget);
  const contactButtonLabel = canTalkLive
    ? 'Talk to a human (live)'
    : 'Leave a message';
  const contactAvailabilityLabel = canTalkLive
    ? 'Available now'
    : 'Offline — message anytime';

  const salesRepAvatarUrl = useMemo(() => {
    const entry = salesRepAvailable?.entry;
    if (!entry) return null;
    return (
      entry.profilePhoto ||
      entry.profilePhotoUrl ||
      entry.photo ||
      entry.avatar ||
      entry.avatarUrl ||
      entry.image ||
      entry.profileImage ||
      null
    );
  }, [salesRepAvailable]);

  const conversationList = useMemo(() => {
    if (conversations && conversations.length) return conversations;
    if (conversationsData && conversationsData.length) return conversationsData;
    return [];
  }, [conversations, conversationsData]);

  const unlockPricing = useCallback(() => {
    setPricingVisible(true);
    try {
      sessionStorage.setItem('presencePricingUnlocked', '1');
    } catch {
      /* ignore storage failures */
    }
  }, []);

  const checkoutStatus = useMemo(() => {
    if (!location?.search) return null;
    const value = new URLSearchParams(location.search).get('checkout');
    if (value === 'success' || value === 'cancel') return value;
    return null;
  }, [location.search]);

  useEffect(() => {
    if (!checkoutStatus) return;
    setNebulaCheckoutError('');
  }, [checkoutStatus]);

  const refreshPlayerProfile = useCallback(async () => {
    if (refreshPlayerInFlightRef.current) return;
    if (!player?.token || !updatePlayer) return;
    refreshPlayerInFlightRef.current = true;
    try {
      const res = await axiosConn.get('players/me', {
        headers: { Authorization: `Bearer ${player.token}` },
      });
      const data = res?.data;
      const candidate =
        data?.data?.data ||
        data?.data?.player ||
        data?.player ||
        data?.data ||
        data;
      const hasIdentity =
        candidate && (candidate._id || candidate.playerId || candidate.handle);
      if (hasIdentity) updatePlayer(candidate);
    } catch {
      /* ignore */
    } finally {
      refreshPlayerInFlightRef.current = false;
    }
  }, [player?.token, updatePlayer]);

  useEffect(() => {
    if (checkoutStatus !== 'success') return;
    refreshPlayerProfile();
  }, [checkoutStatus, refreshPlayerProfile]);

  useEffect(() => {
    if (!player?.token || !updatePlayer) return;
    if (lastRefreshTokenRef.current === player.token) return;
    lastRefreshTokenRef.current = player.token;
    refreshPlayerProfile();
  }, [player?.token, refreshPlayerProfile, updatePlayer]);

  useEffect(() => {
    if (presenceActive) setNebulaCheckoutError('');
  }, [presenceActive]);

  useEffect(() => {
    if (!presenceActive) setSdkDownloadError('');
  }, [presenceActive]);

  const handleCopy = useCallback(async (value, key) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!ok) throw new Error('copy-failed');
      }
      setCopiedKey(key);
      window.setTimeout(() => {
        setCopiedKey((prev) => (prev === key ? null : prev));
      }, 1400);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIsGhostTab(
      Boolean(
        window.name &&
          window.name.startsWith(GHOST_TAB_NAME_PREFIX),
      ),
    );
  }, []);

  const triggerHeartbeat = useCallback(() => {
    setHeartbeatActive(true);
    setHeartbeatTick((tick) => tick + 1);
    if (heartbeatTimerRef.current) {
      clearTimeout(heartbeatTimerRef.current);
    }
    heartbeatTimerRef.current = setTimeout(() => {
      setHeartbeatActive(false);
    }, 600);
  }, []);

  const triggerReconnect = useCallback(() => {
    setReconnectActive(true);
    setReconnectTick((tick) => tick + 1);
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }
    reconnectTimerRef.current = setTimeout(() => {
      setReconnectActive(false);
    }, 900);
  }, []);

  const handleSimulateActivity = useCallback(() => {
    window.dispatchEvent(new Event('focus'));
    window.dispatchEvent(new Event('click'));
  }, []);

  const handleSimulateIdle = useCallback(() => {
    if (isIdleSimulating) return;
    setIsIdleSimulating(true);
    window.dispatchEvent(new Event('blur'));
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      window.dispatchEvent(new Event('focus'));
      setIsIdleSimulating(false);
    }, 10000);
  }, [isIdleSimulating]);

  const handleOpenGhostTab = useCallback(() => {
    setGhostBlocked(false);
    setGhostCloseFailed(false);
    const ghost = window.open('/presence/onboarding', '_blank');
    if (!ghost) {
      setGhostBlocked(true);
      setGhostHandleActive(false);
      return;
    }
    try {
      ghost.name = `${GHOST_TAB_NAME_PREFIX}${tabIdRef.current}`;
    } catch {
      /* ignore */
    }
    try {
      ghost.opener = null;
    } catch {
      /* ignore */
    }
    ghostTabRef.current = ghost;
    setGhostHandleActive(true);
  }, []);

  const handleCloseGhostTab = useCallback(() => {
    setGhostCloseFailed(false);
    const ghost = ghostTabRef.current;
    if (ghost && !ghost.closed) {
      try {
        ghost.close();
      } catch {
        /* best-effort */
      }
      window.setTimeout(() => {
        if (ghost.closed) {
          ghostTabRef.current = null;
          setGhostHandleActive(false);
        } else {
          setGhostCloseFailed(true);
        }
      }, 200);
      return;
    }
    if (isGhostTab) {
      try {
        window.close();
      } catch {
        /* best-effort */
      }
      window.setTimeout(() => {
        if (!window.closed) setGhostCloseFailed(true);
      }, 200);
      return;
    }
    setGhostCloseFailed(true);
  }, [isGhostTab]);

  const handleSimulateDisconnect = useCallback(() => {
    if (!socket || isDisconnecting) return;
    setIsDisconnecting(true);
    try {
      socket.disconnect();
    } catch {
      setIsDisconnecting(false);
      return;
    }
    if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
    disconnectTimerRef.current = setTimeout(() => {
      try {
        socket.connect();
      } catch {
        /* ignore */
      }
      setIsDisconnecting(false);
    }, 5000);
  }, [isDisconnecting, socket]);

  const matchesPlayer = useCallback(
    (payload) => {
      if (!payload) return false;
      const candidates = [
        payload.playerId,
        payload._id,
        payload.mongoId,
        payload.handle,
        payload.displayName,
      ]
        .filter(Boolean)
        .map((val) => String(val));
      return candidates.some((val) => playerIdentifiers.has(val));
    },
    [playerIdentifiers],
  );

  const eventTouchesPlayer = useCallback(
    (payload) => {
      if (!payload) return false;
      if (Array.isArray(payload)) return payload.some(matchesPlayer);
      const toArray = (input) => {
        if (!input) return [];
        if (Array.isArray(input)) return input;
        if (typeof input === 'object') return Object.values(input);
        return [];
      };
      const joins = toArray(payload.joins || payload.add);
      const updates = toArray(payload.updates || payload.update);
      const leaves = toArray(payload.leaves || payload.remove);
      if (joins.length || updates.length || leaves.length) {
        return [...joins, ...updates, ...leaves].some(matchesPlayer);
      }
      if (payload.sessions) {
        return Object.values(payload.sessions).some(matchesPlayer);
      }
      if (payload.players) {
        const list = Array.isArray(payload.players)
          ? payload.players
          : Object.values(payload.players);
        return list.some(matchesPlayer);
      }
      if (payload.data) {
        const list = Array.isArray(payload.data)
          ? payload.data
          : Object.values(payload.data);
        return list.some(matchesPlayer);
      }
      return matchesPlayer(payload);
    },
    [matchesPlayer],
  );

  const getSalesRepMatchKeys = useCallback(
    (payload) => {
      if (!payload || !salesRepIdentifiers.size) return [];
      const candidates = [
        payload.playerId,
        payload._id,
        payload.mongoId,
        payload.accountId,
        payload.id,
        payload.handle,
        payload.playerHandle,
        payload.displayName,
        payload.source?._id,
        payload.source?.playerId,
        payload.source?.handle,
        payload.source?.displayName,
      ]
        .filter(Boolean)
        .map((val) => String(val));
      const seen = new Set();
      const matches = [];
      candidates.forEach((value) => {
        const normalized = normalizeIdentifier(value);
        if (!normalized || !salesRepIdentifiers.has(normalized)) return;
        if (seen.has(value)) return;
        seen.add(value);
        matches.push(value);
      });
      return matches;
    },
    [salesRepIdentifiers],
  );

  const buildFallbackEntry = useCallback((payload) => {
    if (!payload) return null;
    const status = payload.status || payload.presenceStatus;
    const normalized = normalizePresenceState(status, payload.online);
    const lastSeen =
      payload.lastSeenAt ??
      payload.lastSeen ??
      payload.lastUpdatedAt ??
      payload.updatedAt ??
      payload.timestamp ??
      payload.wentOfflineAt ??
      Date.now();
    return {
      ...payload,
      status: status || normalized,
      presenceStatus: normalized,
      online: normalized !== 'offline',
      lastSeenAt: lastSeen,
      lastSeen,
      updatedAt: lastSeen,
    };
  }, []);

  const upsertFallbackPresence = useCallback(
    (payload) => {
      if (!payload || !salesRepIdentifiers.size) return;
      const normalizedPayload =
        typeof payload === 'string' || typeof payload === 'number'
          ? { playerId: String(payload) }
          : payload;
      const matchKeys = getSalesRepMatchKeys(normalizedPayload);
      if (!matchKeys.length) return;
      const entry = buildFallbackEntry(normalizedPayload);
      if (!entry) return;
      setFallbackPresenceMap((prev) => {
        const next = new Map(prev);
        matchKeys.forEach((key) => {
          next.set(String(key), entry);
        });
        return next;
      });
    },
    [buildFallbackEntry, getSalesRepMatchKeys, salesRepIdentifiers],
  );

  const lastEventLabel = useMemo(() => {
    if (!lastPresenceEvent) return '—';
    const time = new Date(lastPresenceEvent.at).toLocaleTimeString();
    return `${lastPresenceEvent.type} @ ${time}`;
  }, [lastPresenceEvent]);

  const lastSocketLabel = useMemo(() => {
    if (!lastSocketEvent) return '—';
    const time = new Date(lastSocketEvent.at).toLocaleTimeString();
    return `${lastSocketEvent.type} @ ${time}`;
  }, [lastSocketEvent]);

  const payloadSummary = useMemo(() => {
    if (!lastPresenceEvent?.payload) return 'No socket payload yet.';
    const payload = lastPresenceEvent.payload;
    const typeLabel = lastPresenceEvent.type || 'presence:event';
    if (Array.isArray(payload)) {
      return `${typeLabel} | items: ${payload.length}`;
    }
    if (payload === null || typeof payload !== 'object') {
      return `${typeLabel} | value: ${String(payload)}`;
    }
    const toArray = (input) => {
      if (!input) return [];
      if (Array.isArray(input)) return input;
      if (typeof input === 'object') return Object.values(input);
      return [];
    };
    const joins = toArray(payload.joins || payload.add);
    const updates = toArray(payload.updates || payload.update);
    const leaves = toArray(payload.leaves || payload.remove);
    const parts = [typeLabel];
    if (joins.length) parts.push(`joins: ${joins.length}`);
    if (updates.length) parts.push(`updates: ${updates.length}`);
    if (leaves.length) parts.push(`leaves: ${leaves.length}`);
    if (!joins.length && !updates.length && !leaves.length) {
      const sessions = payload.sessions ? toArray(payload.sessions).length : 0;
      const players = payload.players ? toArray(payload.players).length : 0;
      const data = payload.data ? toArray(payload.data).length : 0;
      if (sessions) parts.push(`sessions: ${sessions}`);
      if (players) parts.push(`players: ${players}`);
      if (data) parts.push(`data: ${data}`);
    }
    const idCandidate =
      payload.playerId ||
      payload._id ||
      payload.mongoId ||
      payload.handle ||
      payload.displayName;
    if (idCandidate) parts.push(`player: ${String(idCandidate)}`);
    if (parts.length === 1) {
      const keys = Object.keys(payload).slice(0, 4);
      if (keys.length) parts.push(`keys: ${keys.join(', ')}`);
    }
    return parts.join(' | ');
  }, [lastPresenceEvent]);

  const payloadJson = useMemo(() => {
    if (!lastPresenceEvent?.payload) return '';
    try {
      const json = JSON.stringify(lastPresenceEvent.payload, null, 2);
      if (json.length <= 4000) return json;
      return `${json.slice(0, 4000)}\n... (truncated)`;
    } catch {
      return String(lastPresenceEvent.payload);
    }
  }, [lastPresenceEvent]);

  const callStatusLabel = useMemo(() => {
    if (callLoading) return 'Opening Messenger...';
    if (callError) return callError;
    return 'No live reps online or idle right now.';
  }, [callError, callLoading]);

  const storeDraftMessage = useCallback((message, targetId, convoId) => {
    if (!message || !targetId || typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(PRESENCE_DRAFT_KEY, message);
      sessionStorage.setItem(PRESENCE_DRAFT_TARGET_KEY, targetId);
      if (convoId) {
        sessionStorage.setItem(PRESENCE_DRAFT_CONVO_KEY, convoId);
      }
    } catch {
      /* ignore storage failures */
    }
  }, []);

  const showSalesRepBadge = Boolean(
    salesRepAvailable && !callLoading && !callError,
  );
  const salesRepAvatarSrc = salesRepAvatarUrl || DEFAULT_AVATAR_URL;

  const startRepConversation = useCallback(
    async (target, options = {}) => {
      if (!target || !player?.token || !playerId) return;
      if (callLoading) return;
      setCallError('');
      setCallLoading(true);
      try {
        const repHandle = target.handle;
        let repUserId = pickPresenceId(target.entry) || target.id || null;
        if (!repUserId && SALES_REP_IDS.length) {
          repUserId = SALES_REP_IDS.find((val) => HEX_ID_RE.test(val)) || null;
        }
        if (!repUserId && repHandle) {
          const res = await axiosConn.get(
            `players/?playerId=${encodeURIComponent(repHandle)}`,
            {
              headers: { Authorization: `Bearer ${player.token}` },
            },
          );
          repUserId = res?.data?.data?.data?.[0]?._id || null;
        }
        if (!repUserId) {
          throw new Error('rep-id-missing');
        }
        let convo =
          conversationList.find((c) => c?.members?.includes(repUserId)) || null;
        if (!convo) {
          const res = await axiosConn.get(
            `conversations/find/${playerId}/${repUserId}`,
            { headers: { Authorization: `Bearer ${player.token}` } },
          );
          convo = res?.data || null;
        }
        if (!convo) {
          const res = await axiosConn.post(
            'conversations',
            { senderId: playerId, receiverId: repUserId },
            { headers: { Authorization: `Bearer ${player.token}` } },
          );
          convo = res?.data?.data?.data || res?.data?.data || res?.data || null;
        }
        const convoId =
          convo?._id || convo?.conversationId || convo?.id || null;
        if (!convoId) {
          throw new Error('convo-id-missing');
        }
        if (options.prefillMessage) {
          storeDraftMessage(options.prefillMessage, repUserId, convoId);
        }
        window.location.href = `/messenger/${convoId}`;
      } catch (err) {
        setCallError('Unable to open Messenger right now.');
      } finally {
        setCallLoading(false);
      }
    },
    [callLoading, conversationList, player, playerId, storeDraftMessage],
  );

  const handleContactClick = useCallback(() => {
    if (!supportRepTarget) return;
    const prefillMessage = canTalkLive ? null : PRESENCE_DRAFT_MESSAGE;
    startRepConversation(
      supportRepTarget,
      prefillMessage ? { prefillMessage } : {},
    );
  }, [canTalkLive, startRepConversation, supportRepTarget]);

  const handleNebulaCheckout = useCallback(async () => {
    if (nebulaCheckoutLoading || presenceActive) return;
    if (!player?.token) {
      setNebulaCheckoutError('Unable to start checkout. Please try again.');
      return;
    }
    setNebulaCheckoutError('');
    setNebulaCheckoutLoading(true);
    try {
      const res = await axiosConn.post(
          'stripe/presence/nebula/checkout',
        {},
        {
          headers: { Authorization: `Bearer ${player.token}` },
        },
      );
      const url = res?.data?.url;
      if (!url) {
        throw new Error('missing-url');
      }
      window.location.href = url;
    } catch {
      setNebulaCheckoutError('Unable to start checkout. Please try again.');
    } finally {
      setNebulaCheckoutLoading(false);
    }
  }, [nebulaCheckoutLoading, player?.token, presenceActive]);

  const handleSdkDownload = useCallback(async () => {
    if (sdkDownloadLoading || !presenceActive) return;
    if (!player?.token) {
      setSdkDownloadError('Unable to generate download link. Please try again.');
      return;
    }
    setSdkDownloadError('');
    setSdkDownloadLoading(true);
    try {
      const res = await axiosConn.get('presence/sdk/download', {
        headers: { Authorization: `Bearer ${player.token}` },
      });
      const url =
        res?.data?.downloadUrl ||
        res?.data?.data?.downloadUrl ||
        null;
      if (!url) throw new Error('missing-download-url');
      const filename = (() => {
        try {
          const parsed = new URL(url);
          const name = decodeURIComponent(
            (parsed.pathname || '').split('/').pop() || '',
          );
          return name || null;
        } catch {
          const fallback = String(url).split('?')[0];
          const name = fallback.split('/').pop();
          return name || null;
        }
      })();
      if (filename) setSdkFilename(filename);
      window.location.href = url;
    } catch (err) {
      if (err?.response?.status === 403) {
        setSdkDownloadError(
          'SDK access is not available for your account.',
        );
      } else {
        setSdkDownloadError('Unable to generate download link. Please try again.');
      }
    } finally {
      setSdkDownloadLoading(false);
    }
  }, [presenceActive, player?.token, sdkDownloadLoading]);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('presencePricingUnlocked');
      if (saved === '1') setPricingVisible(true);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleFocus = () => {
      const ghost = ghostTabRef.current;
      if (ghost && ghost.closed) {
        ghostTabRef.current = null;
        setGhostHandleActive(false);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const handleConnect = () => {
      setConnectionStatus('Live');
      setLastSocketEvent({ type: 'socket:connect', at: Date.now() });
      if (hasConnectedRef.current && wasDisconnectedRef.current) {
        triggerReconnect();
      }
      wasDisconnectedRef.current = false;
      hasConnectedRef.current = true;
    };
    const handleDisconnect = () => {
      setConnectionStatus('Offline');
      setLastSocketEvent({ type: 'socket:disconnect', at: Date.now() });
      wasDisconnectedRef.current = true;
    };
    const handleReconnectAttempt = () => {
      if (!socket.connected) {
        setConnectionStatus('Reconnecting');
        setLastSocketEvent({
          type: 'socket:reconnect_attempt',
          at: Date.now(),
        });
      }
    };
    const handleReconnectFailed = () => {
      if (!socket.connected) {
        setConnectionStatus('Offline');
        setLastSocketEvent({
          type: 'socket:reconnect_failed',
          at: Date.now(),
        });
      }
    };
    const manager = socket.io;

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    manager?.on('reconnect_attempt', handleReconnectAttempt);
    manager?.on('reconnect_error', handleReconnectAttempt);
    manager?.on('reconnect_failed', handleReconnectFailed);

    if (socket.connected) {
      setConnectionStatus('Live');
      hasConnectedRef.current = true;
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      manager?.off('reconnect_attempt', handleReconnectAttempt);
      manager?.off('reconnect_error', handleReconnectAttempt);
      manager?.off('reconnect_failed', handleReconnectFailed);
    };
  }, [socket, triggerReconnect]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleHeartbeat = (payload) => {
      if (eventTouchesPlayer(payload)) {
        setLastPresenceEvent({
          type: 'presence:heartbeat',
          at: Date.now(),
          payload,
        });
        triggerHeartbeat();
      }
    };
    const handleUpdate = (payload) => {
      if (eventTouchesPlayer(payload)) {
        setLastPresenceEvent({
          type: 'presence:update',
          at: Date.now(),
          payload,
        });
      }
    };
    const handleDiff = (payload) => {
      if (eventTouchesPlayer(payload)) {
        setLastPresenceEvent({
          type: 'presence:diff',
          at: Date.now(),
          payload,
        });
      }
    };
    const handleSnapshot = (payload) => {
      if (eventTouchesPlayer(payload)) {
        setLastPresenceEvent({
          type: 'presence:snapshot',
          at: Date.now(),
          payload,
        });
      }
    };

    socket.on('presence:heartbeat', handleHeartbeat);
    socket.on('presence:update', handleUpdate);
    socket.on('presence:diff', handleDiff);
    socket.on('presence:snapshot', handleSnapshot);

    return () => {
      socket.off('presence:heartbeat', handleHeartbeat);
      socket.off('presence:update', handleUpdate);
      socket.off('presence:diff', handleDiff);
      socket.off('presence:snapshot', handleSnapshot);
    };
  }, [eventTouchesPlayer, socket, triggerHeartbeat]);

  useEffect(() => {
    if (!socket || !salesRepIdentifiers.size) return undefined;
    const handlePlayer = (payload) => upsertFallbackPresence(payload);
    const handleUpdate = (payload) => {
      if (Array.isArray(payload)) {
        payload.forEach((entry) => upsertFallbackPresence(entry));
        return;
      }
      upsertFallbackPresence(payload);
    };
    const handleDiff = (payload) => {
      if (Array.isArray(payload)) {
        payload.forEach((entry) => {
          if (!entry) return;
          const offlineEntry =
            typeof entry === 'object'
              ? { ...entry, status: 'offline', online: false }
              : { playerId: String(entry), status: 'offline', online: false };
          upsertFallbackPresence(offlineEntry);
        });
        return;
      }
      const updates = []
        .concat(payload?.add || [])
        .concat(payload?.update || [])
        .concat(payload?.updates || [])
        .concat(payload?.joins || []);
      updates.forEach((entry) => upsertFallbackPresence(entry));
      const leaves = []
        .concat(payload?.remove || [])
        .concat(payload?.leaves || []);
      leaves.forEach((entry) => {
        if (!entry) return;
        const offlineEntry =
          typeof entry === 'object'
            ? { ...entry, status: 'offline', online: false }
            : { playerId: String(entry), status: 'offline', online: false };
        upsertFallbackPresence(offlineEntry);
      });
    };
    const handleSnapshot = (payload) => {
      const players = payload?.players || payload?.data || payload || [];
      const list = Array.isArray(players)
        ? players
        : Object.entries(players).map(([, val]) => val);
      if (!list.length) return;
      setFallbackPresenceMap((prev) => {
        let next = new Map(prev);
        let changed = false;
        list.forEach((entry) => {
          if (!entry) return;
          const matchKeys = getSalesRepMatchKeys(entry);
          if (!matchKeys.length) return;
          const normalizedEntry = buildFallbackEntry(entry);
          if (!normalizedEntry) return;
          matchKeys.forEach((key) => {
            next.set(String(key), normalizedEntry);
            changed = true;
          });
        });
        return changed ? next : prev;
      });
    };

    socket.on('presence:player', handlePlayer);
    socket.on('player:status', handlePlayer);
    socket.on('presence:update', handleUpdate);
    socket.on('presence:diff', handleDiff);
    socket.on('presence:remove', handleDiff);
    socket.on('presence:remove:detail', handleDiff);
    socket.on('presence:snapshot', handleSnapshot);
    socket.on('player-snapshot', handleSnapshot);

    if (!requestedSalesRepSnapshotRef.current) {
      socket.emit('presence:snapshot:request');
      requestedSalesRepSnapshotRef.current = true;
    }

    return () => {
      socket.off('presence:player', handlePlayer);
      socket.off('player:status', handlePlayer);
      socket.off('presence:update', handleUpdate);
      socket.off('presence:diff', handleDiff);
      socket.off('presence:remove', handleDiff);
      socket.off('presence:remove:detail', handleDiff);
      socket.off('presence:snapshot', handleSnapshot);
      socket.off('player-snapshot', handleSnapshot);
    };
  }, [buildFallbackEntry, getSalesRepMatchKeys, salesRepIdentifiers, socket, upsertFallbackPresence]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel('jg_presence_onboarding_tabs');
    const tabs = new Map();
    const mark = (id, ts) => {
      tabs.set(id, ts || Date.now());
    };
    const prune = () => {
      const cutoff = Date.now() - 15000;
      tabs.forEach((ts, id) => {
        if (ts < cutoff) tabs.delete(id);
      });
    };
    const sync = () => {
      mark(tabIdRef.current, Date.now());
      prune();
      setTabCount(Math.max(1, tabs.size));
    };
    const post = (type) => {
      channel.postMessage({
        type,
        id: tabIdRef.current,
        ts: Date.now(),
      });
    };

    mark(tabIdRef.current, Date.now());
    post('hello');
    sync();

    channel.onmessage = (event) => {
      const msg = event.data;
      if (!msg?.id) return;
      if (msg.type === 'bye') {
        tabs.delete(msg.id);
        sync();
        return;
      }
      mark(msg.id, msg.ts);
      if (msg.type === 'hello') post('ack');
      sync();
    };

    const heartbeat = setInterval(() => {
      post('ping');
      sync();
    }, 5000);

    const handleUnload = () => post('bye');
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      handleUnload();
      channel.close();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
      if (heartbeatTimerRef.current) clearTimeout(heartbeatTimerRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, []);

  return (
    <div className="presence-onboarding">
      <div className="presence-onboarding__inner">
        <section className="presence-card presence-card--identity">
          <div className="presence-card__title">JG Presence Test Drive</div>
          <div className="presence-card__copy">
            <p>
              You are now an active player inside the live JG Presence Engine.
            </p>
            <p>
              This screen lets you intentionally trigger presence behavior and watch it propagate.
            </p>
          </div>
          <div className="presence-chip-row">
            <div className="presence-chip">Player: @{playerHandle}</div>
            <div
              className="presence-chip"
              data-status={connectionStatus.toLowerCase()}
            >
              Connection: {connectionStatus}
            </div>
            <div className="presence-chip">
              Tabs detected: {tabCount} local
            </div>
            {isGhostTab && (
              <div className="presence-chip presence-chip--ghost">Ghost tab</div>
            )}
          </div>
        </section>

        <section className="presence-card">
          <div className="presence-card__title">JG Presence Test Controls</div>
          <div className="presence-controls">
            <button
              type="button"
              className="presence-button"
              onClick={handleSimulateActivity}
            >
              Trigger Activity Signal
            </button>
            <button
              type="button"
              className="presence-button"
              onClick={handleSimulateIdle}
              disabled={isIdleSimulating}
            >
              Simulate Idle
            </button>
            <button
              type="button"
              className="presence-button presence-button--ghost"
              onClick={handleOpenGhostTab}
            >
              Open Ghost Tab
            </button>
            <button
              type="button"
              className="presence-button presence-button--ghost"
              onClick={handleCloseGhostTab}
              disabled={!ghostHandleActive && !isGhostTab}
            >
              Close Ghost Tab
            </button>
            <button
              type="button"
              className="presence-button presence-button--danger"
              onClick={handleSimulateDisconnect}
              disabled={isDisconnecting}
            >
              Simulate Disconnect
            </button>
          </div>
          {ghostBlocked && (
            <div className="presence-helper" role="status">
              Pop-up blocked. Open this page in a second tab (split view if available) to observe multi-tab behavior.
            </div>
          )}
          {ghostCloseFailed && (
            <div className="presence-helper" role="status">
              Only script-opened tabs can be closed automatically.
            </div>
          )}
        </section>

        <section className="presence-card presence-card--pulse">
          <div className="presence-card__title">JG Presence Pulse</div>
          <div className="presence-pulse">
            <div
              className={`pulse-stage pulse-stage--${pulseState}${
                heartbeatActive ? ' pulse-stage--heartbeat' : ''
              }${multiTabActive ? ' pulse-stage--multi' : ''}`}
            >
              <div className="pulse-orb">
                <div className="pulse-core" />
                <div className="pulse-core-glow" />
                {heartbeatActive && (
                  <span
                    key={heartbeatTick}
                    className="pulse-heartbeat"
                    aria-hidden="true"
                  />
                )}
              </div>
              {multiTabActive && (
                <>
                  <span className="pulse-echo pulse-echo--one" aria-hidden="true" />
                  <span className="pulse-echo pulse-echo--two" aria-hidden="true" />
                </>
              )}
              {reconnectActive && (
                <span
                  key={reconnectTick}
                  className="pulse-burst"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="presence-pulse-copy">
              Presence Pulse reacts directly to live presence signals.
            </div>
          </div>
        </section>

        <section className="presence-card">
          <div className="presence-card__title">Live JG Presence HUD</div>
          <div className="presence-hud">
            <div className="presence-hud-row">
              <span className="presence-hud-label">Current presence state</span>
              <span className={`presence-hud-value presence-hud-value--${presenceState}`}>
                {presenceState}
              </span>
            </div>
            <div className="presence-hud-row">
              <span className="presence-hud-label">Local tabs (client-side)</span>
              <span className="presence-hud-value">{tabCount}</span>
            </div>
            <div className="presence-hud-row">
              <span className="presence-hud-label">Last presence event received</span>
              <span className="presence-hud-value">{lastEventLabel}</span>
            </div>
            <div className="presence-hud-row">
              <span className="presence-hud-label">Last socket event</span>
              <span className="presence-hud-value">{lastSocketLabel}</span>
            </div>
            <div className="presence-hud-row presence-hud-row--note">
              <span className="presence-hud-label">
                This is the exact JG Presence socket payload received by your client.
              </span>
            </div>
            <div className="presence-hud-row presence-hud-row--stack">
              <span className="presence-hud-label">Live socket payload</span>
              <div className="presence-hud-value presence-hud-value--payload">
                <div className="presence-hud-payload-preview">
                  <span>{payloadSummary}</span>
                  {lastPresenceEvent?.payload && (
                    <button
                      type="button"
                      className="presence-payload-toggle"
                      onClick={() => setShowPayload((prev) => !prev)}
                      aria-expanded={showPayload}
                      aria-controls="presence-live-payload"
                    >
                      {showPayload
                        ? 'Close live socket payload'
                        : 'Show live socket payload'}
                    </button>
                  )}
                </div>
                {showPayload && lastPresenceEvent?.payload && (
                  <pre
                    id="presence-live-payload"
                    className="presence-hud-payload"
                  >
                    {payloadJson}
                  </pre>
                )}
              </div>
            </div>
            <div className="presence-hud-row">
              <span className="presence-hud-label">Connection status</span>
              <span className="presence-hud-value">{connectionStatus}</span>
            </div>
          </div>
        </section>

        <section className="presence-card">
          <div className="presence-card__title">JG Presence Lens Context</div>
          <div className="presence-card__copy">
            <p>
              Spatial presence is always visible via OrbitingFriends, which is mounted globally under navigation and reflects your live presence state everywhere in the app.
            </p>
          </div>
          <div className="presence-links">
            <Link
              to="/presence-demo"
              target="_blank"
              rel="noopener noreferrer"
              className="presence-link"
            >
              Social Presence
            </Link>
            <Link
              to="/presence-directory"
              target="_blank"
              rel="noopener noreferrer"
              className="presence-link"
            >
              Directory View
            </Link>
            <Link
              to="/presence-admin"
              target="_blank"
              rel="noopener noreferrer"
              className="presence-link"
            >
              Operator View
            </Link>
          </div>
        </section>

        <section
          className={`presence-card presence-card--integrations${
            pricingVisible ? ' presence-card--integrations-dim' : ''
          }`}
        >
          <div className="presence-card__title">Integrate JG Presence into your universe</div>
          <div className="presence-integration-grid">
            <div className="presence-integration-card">
              <div className="presence-integration-title">🌌 Galaxy Overlay (Embedded SDK)</div>
              <p>
                Presence lives inside your app&mdash;on every page, component, or experience you choose.
              </p>
              <p>
                Presence is a first-class, user-visible feature tightly coupled to your UI.
              </p>
              <p>
                Your product controls where and how presence is expressed in real time.
              </p>
              <p className="presence-integration-example">
                &ldquo;Show online state across my entire product, no matter where users navigate.&rdquo;
              </p>
              <a
                href="https://presence-sdk-demo.joegalaxy.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="presence-button"
                onClick={unlockPricing}
              >
                Open SDK demo
              </a>
            </div>
            <div className="presence-integration-card">
              <div className="presence-integration-title">🛰 JG Presence Relay (Federated Service)</div>
              <p>
                Your app sends presence signals. JG Presence Engine handles aggregation, distribution, and state logic.
              </p>
              <p>
                Presence operates as shared infrastructure, not a global UI feature.
              </p>
              <p>
                Your systems consume presence where needed without embedding it everywhere.
              </p>
              <p className="presence-integration-example">
                &ldquo;My app reports activity and connection events, but I don&rsquo;t want to manage multi-tab or idle logic.&rdquo;
              </p>
              <Link
                to="/presence/integration"
                target="_blank"
                rel="noopener noreferrer"
                className="presence-button"
                onClick={unlockPricing}
              >
                View integration options
              </Link>
            </div>
            <div className="presence-integration-card">
              <div className="presence-integration-title">🪐 Signal Observatory (Observability / Augmentation)</div>
              <p>
                Presence is consumed as structured data for analytics, moderation, or UX decisions.
              </p>
              <p>
                Presence is treated as signal data, not interaction, and is never rendered as UI.
              </p>
              <p className="presence-integration-example">
                &ldquo;Trigger features when enough users are online or analyze engagement patterns.&rdquo;
              </p>
              <Link
                to="/presence/integration"
                target="_blank"
                rel="noopener noreferrer"
                className="presence-button"
                onClick={unlockPricing}
              >
                View integration options
              </Link>
            </div>
          </div>
          </section>

            {presenceActive && (
              <div className="presence-pricing-onboard">
                <div className="presence-pricing-subtitle">Get live with JG Presence (&asymp;15 minutes)</div>
                <ol className="presence-onboard-steps">
                  <li>
                    <div className="presence-step-title">Install the JG Presence SDK</div>
                    <div className="presence-step-code">
                      <code>{sdkInstallCommand}</code>
                      <button
                        type="button"
                        className="presence-copy-button"
                        onClick={() => handleCopy(sdkInstallCommand, 'install-command')}
                        data-copied={copiedKey === 'install-command'}
                      >
                        {copiedKey === 'install-command' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="presence-step-note">
                      Run this from your project root. No global installs.
                    </div>
                  </li>
                  <li>
                    <div className="presence-step-title">
                      Initialize JG Presence with your existing auth
                    </div>
                    <div className="presence-step-note">
                      JG Presence does not manage authentication.
                    </div>
                    <div className="presence-step-note">
                      You pass your existing user identity (id + display name) into the Presence Provider.
                    </div>
                    <details className="presence-step-hint">
                      <summary>Helpful detail</summary>
                      <div className="presence-step-note">
                        Most apps pass the same user object they already use for chat, profiles, or sessions.
                      </div>
                    </details>
                  </li>
                  <li>
                    <div className="presence-step-title">
                      Mount the JG Presence provider at app root
                    </div>
                    <div className="presence-step-code presence-step-code--snippet">
                      <pre className="presence-step-snippet">
                        <code>{presenceProviderSnippet}</code>
                      </pre>
                      <button
                        type="button"
                        className="presence-copy-button"
                        onClick={() => handleCopy(presenceProviderSnippet, 'provider-snippet')}
                        data-copied={copiedKey === 'provider-snippet'}
                      >
                        {copiedKey === 'provider-snippet' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="presence-step-note">
                      You only do this once. Presence becomes available everywhere automatically.
                    </div>
                  </li>
                  <li>
                    <div className="presence-step-title">
                      See live presence immediately across your app
                    </div>
                    <div className="presence-step-note">
                      The Presence Test Drive always reflects the currently logged-in player.
                    </div>
                    <div className="presence-step-note">
                      If presence is working, state updates (online, idle, reconnects) will update in real time as you interact with the app.
                    </div>
                    <div className="presence-step-note">
                      This app includes OrbitingFriends as an internal example of a presence-driven UI built on top of JG Presence.
                    </div>
                    <div className="presence-step-note">
                      OrbitingFriends is not part of the SDK — it demonstrates what you can build using the presence state.
                    </div>
                    <div className="presence-step-subtitle">
                      Verify presence in the console
                    </div>
                    <div className="presence-step-code presence-step-code--snippet">
                      <pre className="presence-step-snippet">
                        <code>{presenceDebugSnippet}</code>
                      </pre>
                      <button
                        type="button"
                        className="presence-copy-button"
                        onClick={() =>
                          handleCopy(presenceDebugSnippet, 'debug-snippet')
                        }
                        data-copied={copiedKey === 'debug-snippet'}
                      >
                        {copiedKey === 'debug-snippet' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="presence-step-note">
                      Drop this component anywhere in your app while testing.
                    </div>
                    <div className="presence-step-note">
                      You should see presence updates logged in real time.
                    </div>
                    <div className="presence-step-subtitle">
                      Type shape (TypeScript)
                    </div>
                    <div className="presence-step-code presence-step-code--snippet">
                      <pre className="presence-step-snippet">
                        <code>{`interface JGPresenceState {\n  status: 'online' | 'idle' | 'offline';\n  lastSeenAt: number;\n  deviceCount: number;\n  networkQuality: 'green' | 'yellow' | 'red';\n}`}</code>
                      </pre>
                      <button
                        type="button"
                        className="presence-copy-button"
                        onClick={() =>
                          handleCopy(
                            `interface JGPresenceState {\n  status: 'online' | 'idle' | 'offline';\n  lastSeenAt: number;\n  deviceCount: number;\n  networkQuality: 'green' | 'yellow' | 'red';\n}`,
                            'presence-type',
                          )
                        }
                        data-copied={copiedKey === 'presence-type'}
                      >
                        {copiedKey === 'presence-type' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="presence-step-note">
                      This is the shape of the object returned by useJGPresence().
                    </div>
                    <div className="presence-step-subtitle">
                      Hook quick reference
                    </div>
                    <div className="presence-step-code presence-step-code--snippet">
                      <pre className="presence-step-snippet">
                        <code>{`const presence = useJGPresence();\n\n// Common checks\npresence.status          // 'online' | 'idle' | 'offline'\npresence.deviceCount     // number of active devices\npresence.networkQuality  // connection health`}</code>
                      </pre>
                      <button
                        type="button"
                        className="presence-copy-button"
                        onClick={() =>
                          handleCopy(
                            `const presence = useJGPresence();\n\n// Common checks\npresence.status          // 'online' | 'idle' | 'offline'\npresence.deviceCount     // number of active devices\npresence.networkQuality  // connection health`,
                            'presence-hook',
                          )
                        }
                        data-copied={copiedKey === 'presence-hook'}
                      >
                        {copiedKey === 'presence-hook' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="presence-step-note">
                      You can use this anywhere in your app to drive UI, logic, or analytics.
                    </div>
                    <div className="presence-step-subtitle">Example output</div>
                    <div className="presence-step-code presence-step-code--snippet">
                      <pre className="presence-step-snippet">
                        <code>{presenceDebugOutput}</code>
                      </pre>
                    </div>
                    <div className="presence-step-note">
                      If this updates when you open or close tabs, presence is working.
                    </div>
                    <div className="presence-step-subtitle">
                      Multi-tab confirmation
                    </div>
                    <div className="presence-step-note">
                      Open your app in a second browser tab. You should see:
                    </div>
                    <ul className="presence-step-list">
                      <li>deviceCount increase</li>
                      <li>status remain "online"</li>
                      <li>console logs update immediately</li>
                    </ul>
                    <div className="presence-step-note">
                      Close one tab to confirm reconnect and cleanup behavior.
                    </div>
                  </li>
                </ol>
                <div className="presence-pricing-note">
                  No migration. No lock-in. You can expand JG Presence as your product grows.
                </div>
                {!sdkIssuedLabel && (
                <div className="presence-pricing-note">
                  Download the current JG Presence SDK package. Access is tied to your active subscription.
                </div>
              )}
              {sdkIssuedLabel && (
                <div className="presence-pricing-note">
                  SDK download link issued on {sdkIssuedLabel}.
                </div>
              )}
              <div className="presence-pricing-cta">
                <button
                  type="button"
                  className={`presence-button${
                    sdkIssuedLabel ? ' presence-button--ghost' : ''
                  }`}
                  onClick={handleSdkDownload}
                  disabled={sdkDownloadLoading}
                  aria-busy={sdkDownloadLoading}
                >
                  {sdkIssuedLabel ? 'Download again' : 'Download JG Presence SDK'}
                </button>
              </div>
                {sdkDownloadError && (
                  <div className="presence-helper" role="status">
                    {sdkDownloadError}
                  </div>
                )}
                <div className="presence-step-success">
                  You&rsquo;re live when presence updates appear in real time.
                </div>
              </div>
            )}

          {pricingVisible && (
            <section className="presence-card presence-card--pricing">
              <div className="presence-card__title">Pricing</div>
              <div className="presence-pricing-lede">
              All plans are usage-based. You only pay for active presence.
            </div>
            <div className="presence-pricing-grid">
              <div className="presence-pricing-tier presence-pricing-tier--featured">
                <div className="presence-pricing-name">Nebula (Starter &mdash; self-serve)</div>
                <div className="presence-pricing-price">$49 / month</div>
                <div className="presence-pricing-helper">Start here. Upgrade anytime.</div>
                <ul className="presence-pricing-list">
                  <li>
                    <span className="presence-pricing-key">Includes:</span> Up to 1,000 JG Presence participants / month
                  </li>
                  <li>
                    <span className="presence-pricing-key">Includes:</span> Up to 100 concurrent JG Presence sessions
                  </li>
                  <li>
                    <span className="presence-pricing-key">Includes:</span> Usage-based overage (see below)
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence capabilities:</span> Integration modes: Galaxy Overlay, Presence Relay
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence guarantees:</span> Best-effort delivery
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence observability:</span> Online / idle / offline state; Connection and disconnect events
                  </li>
                  <li>
                    <span className="presence-pricing-key">Support tier:</span> Starter (async)
                  </li>
                  <li>
                    <span className="presence-pricing-key">15-minute confidence tip:</span> Most teams ship Nebula in under 15 minutes.
                  </li>
                  <li>
                    <span className="presence-pricing-key">Upgrade trigger:</span> Teams typically upgrade when JG Presence becomes critical to user experience or workflows.
                  </li>
                </ul>
              </div>
              <div className="presence-pricing-tier">
                <div className="presence-pricing-name">Orbit (Growth &mdash; production)</div>
                <div className="presence-pricing-price">$149 / month</div>
                <ul className="presence-pricing-list">
                  <li>
                    <span className="presence-pricing-key">Includes:</span> Up to 10,000 JG Presence participants / month
                  </li>
                  <li>
                    <span className="presence-pricing-key">Includes:</span> Up to 1,000 concurrent JG Presence sessions
                  </li>
                  <li>
                    <span className="presence-pricing-key">Includes:</span> Usage-based overage
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence capabilities:</span> Integration modes: Galaxy Overlay, Presence Relay, Signal Observatory
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence guarantees:</span> Priority delivery; Improved reconnect handling
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence observability:</span> Presence transitions; Multi-tab behavior; Idle and reconnect visibility
                  </li>
                  <li>
                    <span className="presence-pricing-key">Support tier:</span> Growth (priority async)
                  </li>
                  <li>
                    <span className="presence-pricing-key">Upgrade trigger:</span> Teams typically upgrade when JG Presence powers real-time features or availability decisions.
                  </li>
                </ul>
              </div>
              <div className="presence-pricing-tier">
                <div className="presence-pricing-name">Galaxy (Platform &mdash; ecosystem / enterprise)</div>
                <div className="presence-pricing-price">Starting at $499 / month</div>
                <ul className="presence-pricing-list">
                  <li>
                    <span className="presence-pricing-key">Includes:</span> Custom usage bands
                  </li>
                  <li>
                    <span className="presence-pricing-key">Includes:</span> Optional annual commitment
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence capabilities:</span> Integration modes: Galaxy Overlay, Presence Relay, Signal Observatory
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence guarantees:</span> Platform-level reliability; SLA options
                  </li>
                  <li>
                    <span className="presence-pricing-key">JG Presence observability:</span> Full JG Presence event stream; System-level diagnostics; Advanced presence signals
                  </li>
                  <li>
                    <span className="presence-pricing-key">Support tier:</span> Platform (direct + live)
                  </li>
                  <li>
                    <span className="presence-pricing-key">Upgrade trigger:</span> Teams typically upgrade when JG Presence spans multiple products, teams, or user populations.
                  </li>
                </ul>
              </div>
            </div>
            <div className="presence-pricing-overage">
              <div className="presence-pricing-subtitle">Usage-based overage</div>
              <ul className="presence-pricing-list">
                <li>Participants: $5 per additional 1,000 JG Presence participants</li>
                <li>Concurrent sessions: $10 per additional 100 concurrent sessions</li>
              </ul>
              <div className="presence-pricing-note">
                Overage is metered automatically. There are no surprise charges.
              </div>
            </div>
            <div className="presence-pricing-checkout">
              <div className="presence-pricing-subtitle">Activate JG Presence &mdash; Nebula</div>
              <p>
                You&rsquo;re activating JG Presence for your app.
                Nebula is self-serve and usage-based &mdash; you can upgrade or cancel anytime.
              </p>
              <ul className="presence-pricing-checklist">
                <li>&#10003; Live JG Presence in your app</li>
                <li>&#10003; Galaxy Overlay + Presence Relay</li>
                <li>&#10003; Usage-based pricing</li>
                <li>&#10003; Upgrade anytime</li>
              </ul>
              <div className="presence-pricing-confirm">
                Most teams see JG Presence working in minutes.
              </div>
              {!presenceActive && (
                <div className="presence-pricing-cta">
                  <button
                    type="button"
                    className="presence-button"
                    onClick={handleNebulaCheckout}
                    disabled={nebulaCheckoutLoading}
                    aria-busy={nebulaCheckoutLoading}
                  >
                    Pay & activate Nebula
                  </button>
                </div>
              )}
              {nebulaCheckoutError && (
                <div className="presence-helper" role="status">
                  Unable to start checkout. Please try again.
                </div>
              )}
            </div>
            <div className="presence-pricing-cta">
              <button
                type="button"
                className="presence-button"
                onClick={handleContactClick}
                disabled={!canInitiateContact || callLoading}
              >
                {contactButtonLabel}
              </button>
              <div className="presence-call-helper" role="status">
                {contactAvailabilityLabel}
              </div>
            </div>
              <div className="presence-call-status" role="status">
                {showSalesRepBadge ? (
                  <>
                    <span className="presence-call-label">
                      Live rep available:
                    </span>
                    <span className="presence-rep-chip">
                      <span className="presence-rep-avatar">
                        <img
                          src={salesRepAvatarSrc}
                          alt={`${salesRepAvailable.handle} avatar`}
                          loading="lazy"
                        />
                        <span
                          className={`presence-rep-dot presence-status-dot--${salesRepTone}`}
                          aria-hidden="true"
                        />
                      </span>
                      <span className="presence-rep-handle">
                        @{salesRepAvailable.handle}
                      </span>
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      className={`presence-status-dot presence-status-dot--${salesRepTone}`}
                      aria-hidden="true"
                    />
                    <span>{callStatusLabel}</span>
                  </>
                )}
              </div>
            </section>
          )}
      </div>
    </div>
  );
}
